/**
 * Shared `<OwnershipBadge>` reusable primitive.
 *
 * TNCY-6b promoted this from `features/projects/access/OwnershipBadge.tsx`
 * into `apps/web/src/features/shared/ownership/`. TNCY-9 promotes it again
 * into this cross-app `@aeriosiq/ownership` package so both `apps/web`
 * (Projects / Contracts / Contract Positions / Inspection Instructions) AND
 * `apps/lyse` (Data Acquisitions) render ONE identical row tell — the lyse
 * app cannot import from `apps/web`, and `packages/ui` is barred for
 * business vocabulary (`shared-packages-discipline`), so the cross-tenant
 * ownership primitives live in their own small package.
 *
 * It tells, at a glance, whether a row is the caller's OWN data (lives in
 * their tenant's schema) or a MEMBER row (lives in another tenant, shared in
 * via a cross-tenant grant — XT-D20/D22). The tooltip on a member row names
 * the owning tenant + the caller's role.
 *
 * API (kept stable from TNCY-6):
 *
 *   <OwnershipBadge ownership ownerTenantName role entityNoun />
 *
 *   - `ownership`        "owner" | "member" | undefined (pre-2.4.0 ⇒ owner)
 *   - `ownerTenantName`  owning-tenant display name (member rows)
 *   - `role`             the caller's AccessRole on a member row
 *   - `entityNoun`       owner-tooltip noun ("project" | "contract" | …);
 *                        defaults to "data" so the badge reads sensibly on
 *                        any list. The member tooltip names the owning
 *                        tenant + role and is entity-agnostic.
 *
 * On an `owner` row the badge is intentionally subtle (no chrome-heavy
 * affordance) so own-only lists read exactly as before; a `member` row gets
 * a filled icon + the descriptive tooltip. The accessible label (not
 * colour-only) lives on the `role="img"` span so screen readers and the a11y
 * audit see the ownership semantics.
 */
import type { ReactElement } from "react";
import { Box, Tooltip } from "@mui/material";
import type { AccessRole } from "./roleLabels.js";
import { accessRoleLabel } from "./roleLabels.js";
import { HouseIcon, UsersIcon } from "@aeriosiq/ui/icons";

/** `owner` = the caller's own data (default `scope=own`); `member` = data the
 *  caller was granted cross-tenant read access to (`scope=member`). */
export type Ownership = "owner" | "member";

export interface OwnershipBadgeProps {
  ownership: Ownership | undefined;
  ownerTenantName?: string | null | undefined;
  role?: AccessRole | null | undefined;
  /** Owner-tooltip noun ("project" | "contract" | …). Defaults to "data". */
  entityNoun?: string;
}

export function OwnershipBadge({
  ownership,
  ownerTenantName,
  role,
  entityNoun = "data",
}: OwnershipBadgeProps): ReactElement {
  // Pre-2.4.0 back-compat: a row without an `ownership` tell is the
  // caller's own data.
  const isMember = ownership === "member";

  if (!isMember) {
    const label = `Your ${entityNoun}`;
    return (
      <Tooltip title={label}>
        <Box
          component="span"
          role="img"
          aria-label={label}
          sx={{ display: "inline-flex", color: "text.disabled" }}
        >
          <HouseIcon fontSize="small" aria-hidden />
        </Box>
      </Tooltip>
    );
  }

  // Member row — name the owner + the caller's role.
  const owner = ownerTenantName ?? "another company";
  const roleLabel = role ? accessRoleLabel(role) : null;
  const label = roleLabel
    ? `Shared by ${owner} — your role: ${roleLabel}`
    : `Shared by ${owner}`;

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        role="img"
        aria-label={label}
        sx={{ display: "inline-flex", color: "primary.main" }}
      >
        <UsersIcon fontSize="small" aria-hidden />
      </Box>
    </Tooltip>
  );
}
